import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { allArticles } from "@/mocks/articles";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [articlesOpen, setArticlesOpen] = useState(false);
  const articlesRef = useRef<HTMLDivElement>(null);
  const mobileArticlesRef = useRef<HTMLDivElement>(null);
  const [mobileArticlesOpen, setMobileArticlesOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (articlesRef.current && !articlesRef.current.contains(e.target as Node)) {
        setArticlesOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const closeMobile = () => {
    setMobileOpen(false);
    setMobileArticlesOpen(false);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-background-50/95 backdrop-blur-md border-b border-background-200"
            : "bg-transparent"
        }`}
      >
        <div className="w-full px-4 md:px-6">
          <div className="flex items-center justify-between h-16 md:h-18">
            <Link
              to="/"
              className="cursor-pointer flex items-center gap-2 flex-shrink-0"
              onClick={() => setMobileOpen(false)}
            >
              <span className={`font-heading text-lg md:text-xl font-semibold tracking-tight transition-colors ${
                scrolled ? "text-foreground-950" : "text-white"
              }`}>
                USFansSpreadsheet
              </span>
            </Link>

            <div className="hidden md:flex items-center gap-1">
              <Link
                to="/"
                className={`whitespace-nowrap cursor-pointer px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  scrolled
                    ? "text-foreground-700 hover:text-foreground-950 hover:bg-background-100"
                    : "text-white/80 hover:text-white hover:bg-white/10"
                }`}
              >
                Strona główna
              </Link>

              <div className="relative" ref={articlesRef}>
                <button
                  onClick={() => setArticlesOpen(!articlesOpen)}
                  className={`whitespace-nowrap cursor-pointer px-4 py-2 rounded-full text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${
                    scrolled
                      ? "text-foreground-700 hover:text-foreground-950 hover:bg-background-100"
                      : "text-white/80 hover:text-white hover:bg-white/10"
                  }`}
                >
                  Artykuły SEO
                  <i className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform ${articlesOpen ? "rotate-180" : ""}`}></i>
                </button>

                {articlesOpen && (
                  <div className={`absolute top-full mt-2 right-0 w-72 max-h-72 overflow-y-auto rounded-2xl shadow-lg border py-2 z-50 ${
                    scrolled
                      ? "bg-background-50 border-background-200"
                      : "bg-foreground-900 border-white/10"
                  }`}>
                    {allArticles.map((article) => (
                      <Link
                        key={article.id}
                        to={`/artykul/${article.slug}`}
                        onClick={() => setArticlesOpen(false)}
                        className={`whitespace-nowrap cursor-pointer block px-5 py-2.5 text-sm transition-colors ${
                          scrolled
                            ? "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                            : "text-white/70 hover:bg-white/10 hover:text-white"
                        }`}
                      >
                        {article.title}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className={`whitespace-nowrap cursor-pointer px-4 py-2 rounded-full text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${
                  scrolled
                    ? "text-foreground-700 hover:text-foreground-950 hover:bg-background-100"
                    : "text-white/80 hover:text-white hover:bg-white/10"
                }`}
              >
                Przewodnik zakupów
                <i className="ri-shopping-bag-line w-4 h-4 flex items-center justify-center"></i>
              </a>
            </div>

            <div className="hidden md:flex items-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className={`whitespace-nowrap cursor-pointer px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  scrolled
                    ? "bg-foreground-950 text-background-50 hover:bg-foreground-800"
                    : "bg-white text-foreground-950 hover:bg-white/90"
                }`}
              >
                Buy It Now
              </a>
            </div>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`md:hidden cursor-pointer w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                scrolled
                  ? "text-foreground-950 hover:bg-background-100"
                  : "text-white hover:bg-white/10"
              }`}
              aria-label="Menu"
            >
              {mobileOpen ? (
                <i className="ri-close-line w-6 h-6 flex items-center justify-center"></i>
              ) : (
                <i className="ri-menu-line w-6 h-6 flex items-center justify-center"></i>
              )}
            </button>
          </div>
        </div>
      </nav>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-foreground-950/60 backdrop-blur-sm" onClick={closeMobile}></div>
          <div className="absolute top-0 right-0 bottom-0 w-80 max-w-[85vw] bg-background-50 shadow-2xl overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
              <span className="font-heading text-lg font-semibold text-foreground-950">Menu</span>
              <button
                onClick={closeMobile}
                className="cursor-pointer w-10 h-10 rounded-full flex items-center justify-center text-foreground-500 hover:bg-background-100"
              >
                <i className="ri-close-line w-5 h-5 flex items-center justify-center"></i>
              </button>
            </div>

            <div className="px-4 py-4 space-y-1">
              <Link
                to="/"
                onClick={closeMobile}
                className="cursor-pointer flex items-center gap-3 px-4 py-3 rounded-xl text-foreground-900 hover:bg-background-100 text-sm font-medium transition-colors"
              >
                <span className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center">
                  <i className="ri-home-line w-4 h-4 flex items-center justify-center"></i>
                </span>
                Strona główna
              </Link>

              <button
                onClick={() => setMobileArticlesOpen(!mobileArticlesOpen)}
                className="cursor-pointer w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl text-foreground-900 hover:bg-background-100 text-sm font-medium transition-colors"
              >
                <span className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center">
                    <i className="ri-article-line w-4 h-4 flex items-center justify-center"></i>
                  </span>
                  Artykuły SEO
                </span>
                <i className={`ri-arrow-down-s-line w-5 h-5 flex items-center justify-center transition-transform text-foreground-400 ${mobileArticlesOpen ? "rotate-180" : ""}`}></i>
              </button>

              {mobileArticlesOpen && (
                <div className="pl-12 pr-2 space-y-1 pb-2">
                  {allArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/artykul/${article.slug}`}
                      onClick={closeMobile}
                      className="cursor-pointer block px-3 py-2 rounded-lg text-sm text-foreground-600 hover:text-foreground-900 hover:bg-background-100 transition-colors"
                    >
                      {article.title}
                    </Link>
                  ))}
                </div>
              )}

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={closeMobile}
                className="cursor-pointer flex items-center gap-3 px-4 py-3 rounded-xl text-foreground-900 hover:bg-background-100 text-sm font-medium transition-colors"
              >
                <span className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center">
                  <i className="ri-shopping-bag-line w-4 h-4 flex items-center justify-center"></i>
                </span>
                Przewodnik zakupów
              </a>
            </div>

            <div className="px-4 py-4 border-t border-background-200 mt-4">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={closeMobile}
                className="cursor-pointer flex items-center justify-center gap-2 w-full px-5 py-3 bg-foreground-950 text-background-50 rounded-full text-sm font-medium hover:bg-foreground-800 transition-colors"
              >
                Buy It Now
                <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
              </a>
            </div>

            <div className="px-4 py-6">
              <p className="text-xs text-foreground-400 text-center">
                USFansSpreadsheet 2026
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}